import axiosInstance from './AxiosClient'

const EMAIL_VALIDATION_ENDPOINT = '/donors/validate_email/'

/**
 * Extract domain from email address
 * @param {string} email - Email address
 * @returns {string} Domain or empty string
 */
function extractDomain(email) {
  return email.split('@')[1] || ''
}

/**
 * Create fallback validation result when API is unavailable
 * @param {string} email - Email address
 * @param {string} message - Error message
 * @returns {Object} Fallback validation result
 */
function createFallbackResult(email, message) {
  return {
    valid: true, // Fallback to allowing email if API is unavailable
    disposable: false,
    role: false,
    domain: extractDomain(email),
    message,
    api_available: false,
  }
}

/**
 * Validate an email address in real-time
 * @param {string} email - Email address to validate
 * @returns {Promise<Object>} Validation result with valid, disposable, role, domain, message, and api_available
 */
export async function validateEmail(email) {
  if (!email || !email.trim()) {
    return {
      valid: false,
      disposable: false,
      role: false,
      domain: '',
      message: 'Email address is required.',
      api_available: false,
    }
  }

  try {
    const response = await axiosInstance.get(EMAIL_VALIDATION_ENDPOINT, {
      params: { email: email.trim() },
    })

    return response.data
  } catch (error) {
    // fallback to allowing email if API is unavailable
    if (error.response) {
      // API returned an error response
      const errorData = error.response.data
      return {
        valid: false,
        disposable: false,
        role: false,
        domain: extractDomain(email),
        message: errorData.error || errorData.message || 'Email validation failed.',
        api_available: false,
      }
    } else {
      // Network error or request failed - allow email but log issue
      return createFallbackResult(
        email,
        'Email verification service unavailable. Email format validated only.',
      )
    }
  }
}

/**
 * Check if email validation result indicates the email should be rejected
 * @param {Object} validationResult - Result from validateEmail function
 * @returns {boolean} True if email should be rejected
 */
export function shouldRejectEmail(validationResult) {
  if (!validationResult.api_available) {
    // If API is unavailable, don't reject (fallback to format validation only)
    return false
  }

  // Reject if email is invalid, disposable, or role-based
  return !validationResult.valid || validationResult.disposable || validationResult.role
}

/**
 * Get user-friendly error message from validation result
 * @param {Object} validationResult - Result from validateEmail function
 * @returns {string} Error message or empty string if email is valid
 */
export function getValidationErrorMessage(validationResult) {
  if (!validationResult.api_available) {
    // If API is unavailable, don't show error (fallback mode)
    return ''
  }

  if (!validationResult.valid) {
    return validationResult.message || 'This email address is invalid or does not exist.'
  }

  if (validationResult.disposable) {
    return validationResult.message || 'Disposable email addresses are not allowed.'
  }

  if (validationResult.role) {
    return (
      validationResult.message ||
      'Role-based email addresses (e.g., [email protected]) are not allowed.'
    )
  }

  return '' // Email is valid
}
